/**
 * CBE → bi-directional SED sync adapter (CBE-A3, caller 3).
 *
 * `syncSedContextBindings.ts` historically sourced its rows from the legacy
 * `SED_BINDING_REGISTRY`. This adapter derives the SAME sync surface from
 * the CBE `CONTEXT_BINDING_REGISTRY` so the CBE is the single declarative
 * source of truth, while the sync module's behaviour stays byte-identical
 * (pinned by the `syncSedContextBindings.oracle.test.ts` legacy-equivalence
 * suite).
 *
 * Which rows participate in path-keyed bi-dir sync (`isSedSyncRow`)?
 *   - `targetField.surface === 'sed'` — only SED-path rows are addressable
 *     by an amendment's `variablePath`.
 *   - `factMatcher.kind === 'always'` — span-conditional rows (locusKeyword
 *     / spanRegex) CANNOT be evaluated from the SED side: an amendment has
 *     no source span to match, and including them would change today's
 *     behaviour (the legacy registry keyed on tag only). Extending
 *     context→SED sync to span-conditional rows is a deliberate behaviour
 *     addition, out of scope here (followUp).
 *   - not value-kind excluded (`isSyncExcluded`) — range / multi / date /
 *     enum values have no legacy identity round-trip at the SED boundary
 *     (a range never collapses to a scalar; date/enum re-projections lose
 *     the stated precision/casing), so they stay out of the convergence-
 *     guarded sync loop. No migrated row carries these kinds today.
 *
 * Reverse projections REPLICATE the frozen identity projections from
 * `sedBindingRegistry.ts` (identityNumber / identityString /
 * identityBoolean) rather than importing them: the legacy file is kept
 * byte-frozen as the migration oracle, and the round-trip + oracle tests
 * pin the replicas' equivalence.
 *
 * Pure / synchronous. No I/O.
 */
import type { ClaimCategory, ExtractedClaim } from '../../types/siteContext';
import { isAllowedSedField } from '../siteContext/extractClaims';
import { variablePathToBindingSedPath } from '../siteContext/sedBindingRegistry';
import { CONTEXT_BINDING_REGISTRY } from './contextBindingRegistry';
import type { CbeBinding, CbeValue } from './cbeTypes';

/* ── Reverse (SED value → claim value) identity projections ───────────── */

/** Replica of `sedBindingRegistry.identityNumber` (frozen reference). */
function identityNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

/** Replica of `sedBindingRegistry.identityString` (frozen reference). */
function identityString(v: unknown): string | undefined {
  if (typeof v === 'string') {
    const s = v.trim();
    return s.length > 0 ? s : undefined;
  }
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return undefined;
}

/** Replica of `sedBindingRegistry.identityBoolean` (frozen reference). */
function identityBoolean(v: unknown): boolean | undefined {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const s = v.toLowerCase().trim();
    if (s === 'true' || s === 'yes') return true;
    if (s === 'false' || s === 'no') return false;
  }
  return undefined;
}

/** A reverse projection: SED-side value → claim-domain value. */
export type SyncReverseProjection = (sedValue: unknown) => ExtractedClaim['claimedValue'];

/**
 * Map a binding's COERCION kind to the legacy identity projection that
 * reverses it at the binding boundary (the `toContext` equivalent):
 *   - toNumber / unitNormalize / toPercent / toYears → identityNumber
 *     (all produce scalar numbers; the reverse is numeric identity),
 *   - toBool   → identityBoolean,
 *   - toString → identityString,
 *   - toRange / toDate / toEnum → undefined (sync-excluded kinds — no
 *     loss-free identity round-trip exists; see module header).
 */
export function reverseProjectionFor(binding: CbeBinding): SyncReverseProjection | undefined {
  switch (binding.coercion) {
    case 'toNumber':
    case 'unitNormalize':
    case 'toPercent':
    case 'toYears':
      return identityNumber;
    case 'toBool':
      return identityBoolean;
    case 'toString':
      return identityString;
    case 'toRange':
    case 'toDate':
    case 'toEnum':
      return undefined;
    default: {
      const _exhaustive: never = binding.coercion;
      return _exhaustive;
    }
  }
}

/* ── Sync-surface predicates ──────────────────────────────────────────── */

/** Why a binding row is excluded from path-keyed bi-dir sync, if it is. */
export type SyncExclusion =
  | { readonly excluded: false }
  | { readonly excluded: true; readonly reason: string };

/**
 * Value-kind exclusion from the bi-dir sync loop. The convergence proof
 * (`bindingDeepEqual`) only holds for scalar / bool / complex values that
 * identity-round-trip; the kinds below do not.
 */
export function isSyncExcluded(binding: CbeBinding): SyncExclusion {
  switch (binding.valueKind) {
    case 'range':
      return { excluded: true, reason: 'range values never collapse to a scalar SED slot (no midpoint) — no identity round-trip' };
    case 'multi':
      return { excluded: true, reason: 'multi (count×perUnit) values have no single SED scalar — no identity round-trip' };
    case 'date':
      return { excluded: true, reason: 'date values re-project lossily (stated precision is not recoverable from an ISO scalar)' };
    case 'enum':
      return { excluded: true, reason: 'enum values are span-derived (explicit-in-span guard) — a SED-side write cannot be re-grounded' };
    default:
      return { excluded: false };
  }
}

/**
 * Is this SED path UNRESOLVABLE through `resolveSedField` (outside the SC3
 * allow-list, or naming a section/slot the SED schema does not carry)?
 * Informational: such rows still participate in sync + diff EXACTLY as
 * today — the resolver returns null, so they surface as 'context-only' /
 * draft amendments with `oldValue: null`. Extending `ALLOWED_SED_FIELDS`
 * to make them resolvable is a deliberate behaviour change, out of scope
 * (followUp).
 */
export function sedPathUnavailable(path: string): boolean {
  return !isAllowedSedField(path);
}

/** Does this row participate in path-keyed bi-dir sync? (See module header.) */
export function isSedSyncRow(binding: CbeBinding): boolean {
  return binding.targetField.surface === 'sed'
    && binding.factMatcher.kind === 'always'
    && !isSyncExcluded(binding).excluded;
}

/* ── Precomputed sync surface (registry is a static const) ────────────── */

const SED_SYNC_ROWS: readonly CbeBinding[] = CONTEXT_BINDING_REGISTRY.filter(isSedSyncRow);

const SYNC_ROWS_BY_TAG: ReadonlyMap<ClaimCategory, readonly CbeBinding[]> = (() => {
  const m = new Map<ClaimCategory, CbeBinding[]>();
  for (const b of SED_SYNC_ROWS) {
    const list = m.get(b.sourceTag) ?? [];
    list.push(b);
    m.set(b.sourceTag, list);
  }
  return m as ReadonlyMap<ClaimCategory, readonly CbeBinding[]>;
})();

/**
 * One group per tag (first-occurrence registry order), mirroring a legacy
 * `SedBindingEntry` — `technology.reactor` groups its two path rows.
 */
export interface SedSyncGroup {
  readonly tag: ClaimCategory;
  /** The group's sync rows, in declared (legacy `sedPaths`) order. */
  readonly rows: readonly CbeBinding[];
}

const SYNC_GROUPS: readonly SedSyncGroup[] = (() => {
  const order: ClaimCategory[] = [];
  for (const b of SED_SYNC_ROWS) {
    if (!order.includes(b.sourceTag)) order.push(b.sourceTag);
  }
  return order.map(tag => ({ tag, rows: SYNC_ROWS_BY_TAG.get(tag) ?? [] }));
})();

/**
 * Reverse path → binding index over ALL sed-surface rows, with a
 * deterministic winner when several rows target one path (the
 * `parcel.developableAcres` case — migrated `parcel.boundaries` 'always'
 * row vs the net-new `parcel.acres` locusKeyword row):
 *   1. prefer `factMatcher.kind === 'always'` (the migrated rows),
 *   2. then the lowest `fillPolicy.precedenceRank`,
 *   3. then declared registry order (stable sort).
 */
const SED_PATH_INDEX: ReadonlyMap<string, CbeBinding> = (() => {
  const byPath = new Map<string, CbeBinding[]>();
  for (const b of CONTEXT_BINDING_REGISTRY) {
    if (b.targetField.surface !== 'sed') continue;
    const list = byPath.get(b.targetField.path) ?? [];
    list.push(b);
    byPath.set(b.targetField.path, list);
  }
  const m = new Map<string, CbeBinding>();
  for (const [path, rows] of byPath) {
    const winner = rows
      .slice()
      .sort((a, b) =>
        (a.factMatcher.kind === 'always' ? 0 : 1) - (b.factMatcher.kind === 'always' ? 0 : 1)
        || a.fillPolicy.precedenceRank - b.fillPolicy.precedenceRank,
      )[0]!;
    m.set(path, winner);
  }
  return m;
})();

/* ── Lookups ──────────────────────────────────────────────────────────── */

/** All path-keyed sync rows, in registry order. */
export function sedSyncRows(): readonly CbeBinding[] {
  return SED_SYNC_ROWS;
}

/** The sync rows that fire for a claim category (legacy `getBindingsForCategory`). */
export function sedSyncRowsForTag(tag: ClaimCategory): readonly CbeBinding[] {
  return SYNC_ROWS_BY_TAG.get(tag) ?? [];
}

/** Per-tag groups in registry order (legacy registry iteration shape). */
export function sedSyncGroups(): readonly SedSyncGroup[] {
  return SYNC_GROUPS;
}

/** The full path → winning-binding index (see winner rule above). */
export function sedPathIndex(): ReadonlyMap<string, CbeBinding> {
  return SED_PATH_INDEX;
}

/**
 * Resolve an amendment `variablePath` to its SYNC binding, or `undefined`
 * when the path is unbound OR its winning row is not sync-eligible (e.g. a
 * path only a locusKeyword row targets — `parcel.dataCenterAcres` — which
 * the legacy registry never bound; preserves the legacy no-op). Mirrors
 * legacy `getBindingForSedPath`: exact match first, then the
 * `variablePathToBindingSedPath` normalisation (`parcel.data.acres` →
 * `parcel.acres`).
 */
export function syncBindingForVariablePath(path: string): CbeBinding | undefined {
  const binding = SED_PATH_INDEX.get(path)
    ?? SED_PATH_INDEX.get(variablePathToBindingSedPath(path));
  if (!binding || !isSedSyncRow(binding)) return undefined;
  return binding;
}

/**
 * Project a forward-coerced `CbeValue` onto the legacy `toSed` scalar
 * domain (`string | number | boolean`). Sync-excluded kinds return
 * `undefined` — enforcing `isSyncExcluded` at the value boundary too
 * (defence in depth; sync rows cannot produce them by construction).
 */
export function cbeValueToSyncScalar(value: CbeValue): string | number | boolean | undefined {
  switch (value.kind) {
    case 'scalar':
      return value.value;
    case 'complex':
      return value.value;
    case 'bool':
      return value.value;
    case 'range':
    case 'date':
    case 'enum':
    case 'multi':
      return undefined;
    default: {
      const _exhaustive: never = value;
      return _exhaustive;
    }
  }
}
